import type { Tier } from '../platform/access/tierForBuild'

/**
 * The developer tier override — #477. Follows `backendOverride.ts`'s idiom:
 * an id validated against a fixed set, never a value trusted as-is, storage
 * reached lazily so merely touching it cannot throw at import.
 *
 * **Never in production**, the same way the backend override is never in
 * production. This module has no top-level side effects of its own — only
 * `const` data and functions — so nothing here forces it to ship; what makes
 * production actually free of it is that `platform/access/tier.ts`'s `TIER`
 * only calls `resolveTier` inside `import.meta.env.MODE !== 'production' ?
 * … : …`, the same fold-and-drop mechanism `api/client.ts`'s `API_BASE`
 * uses for `resolveApiBase`. This file supplies the mechanics; the
 * production gate is enforced at that call site and pinned by
 * `main.test.ts`'s production-override test.
 */

/** Stores a tier literal, never anything else. See `readStoredTier`. */
export const TIER_STORAGE_KEY = 'tracke5.developer.tier'

const TIERS: readonly Tier[] = ['free', 'premium']

function isTier(value: string | null): value is Tier {
  return (TIERS as readonly string[]).includes(value ?? '')
}

/** The browser's storage, reached lazily: merely touching `localStorage` throws in some contexts. */
const browserStorage = (): Storage => window.localStorage

/**
 * The stored choice, or `null` for none, unreadable or unrecognised.
 *
 * Validated against the two known tiers rather than trusted — an arbitrary
 * stored string must never reach `entitlementsForTier`, which only
 * understands `'free'` and `'premium'`.
 */
export function readStoredTier(storage: () => Storage = browserStorage): Tier | null {
  try {
    const value = storage().getItem(TIER_STORAGE_KEY)
    return isTier(value) ? value : null
  } catch {
    return null
  }
}

/** `false` when storage refused — the caller must not reload onto a choice that was not saved. */
export function storeTier(tier: Tier, storage: () => Storage = browserStorage): boolean {
  try {
    storage().setItem(TIER_STORAGE_KEY, tier)
    return true
  } catch {
    return false
  }
}

export function clearStoredTier(storage: () => Storage = browserStorage): boolean {
  try {
    storage().removeItem(TIER_STORAGE_KEY)
    return true
  } catch {
    return false
  }
}

/**
 * The tier this page load grants: the stored override if one exists and is
 * readable, otherwise the build's own tier.
 *
 * Takes `buildTier` rather than reading `tierForBuild` itself, so the
 * caller — `platform/access/tier.ts` — is the one place that decides
 * whether this may run at all. See that module for the production gate.
 */
export function resolveTier(buildTier: Tier, storage: () => Storage = browserStorage): Tier {
  return readStoredTier(storage) ?? buildTier
}
